import { calcMinGroupPrice } from "@/lib/calc-min-group-price";
import { isOfferAvailableNow } from "@/lib/is-offer-available-now";
import type { Availability, Category, ItemOffer, Option, OptionGroup } from "@/types";

import { COMBO_MIN_SAVINGS_CENTS } from "./types";

/** Slice of the catalog the combo search needs. */
export type ComboCatalog = {
  itemOffers: ItemOffer[];
  optionGroups: OptionGroup[];
  availabilities: Availability[];
  categoryByOfferId?: Map<string, Pick<Category, "status" | "availabilityId">>;
};

export type ComboSavings = {
  /** The combo item offer itself — the card opens ITS detail page. */
  comboItemOfferId: string;
  comboItemId: string;
  anchorItemId: string;
  suggestedItemId: string;
  /** Integer cents. */
  savingsCents: number;
};

function optionsInGroup(group: OptionGroup): Option[] {
  return group.options ?? [];
}

/**
 * `Price.value` is a decimal reais amount (`12.5`), like everywhere else in
 * this codebase — never integer cents. All combo math below works in
 * integer cents (per the spec: "integer cents internally, formatted once"),
 * so every reais value crosses this boundary exactly once.
 */
function toCents(reais: number): number {
  return Math.round(reais * 100);
}

/**
 * Cheapest way to satisfy every required group of `offer` OTHER than the two
 * carrying the anchor/suggested picks — the customer still has to fill them,
 * so their floor price is part of what the combo actually costs.
 */
function restOfRequiredGroupsMinPriceCents(offer: ItemOffer, groups: OptionGroup[], skipGroupIds: ReadonlySet<string>): number {
  const inUse = groups.filter((group) => offer.optionGroupsId?.includes(group.id) && !skipGroupIds.has(group.id));
  return toCents(inUse.reduce((total, group) => total + calcMinGroupPrice(group, "value"), 0));
}

/**
 * Finds the option, inside a required group of `offer`, whose underlying
 * `itemId` matches `itemId` — i.e. "this combo's group X can be filled with
 * the anchor/suggested item". Required groups only (`minPermitted > 0`):
 * an optional side never turns a purchase into "the combo".
 */
function findRequiredOption(
  offer: ItemOffer,
  groups: OptionGroup[],
  itemId: string,
  excludeGroupId: string | null,
): { group: OptionGroup; option: Option } | null {
  for (const group of groups) {
    if (!offer.optionGroupsId?.includes(group.id)) continue;
    if (group.minPermitted <= 0) continue;
    if (excludeGroupId && group.id === excludeGroupId) continue;

    const option = optionsInGroup(group).find((candidate) => candidate.itemId === itemId);
    if (option) return { group, option };
  }
  return null;
}

/**
 * Combo math (docs/usecases/suggestions.md "Catalog-dependent filters stay in
 * the storefront join"): a combo offer whose required option groups can be
 * filled with BOTH the anchor and the suggested item, each in a DIFFERENT
 * group.
 *
 *   comboPrice = offer.price + anchorOption.price + suggestedOption.price
 *              + Σ minPrice(every other required group)
 *   savings    = anchorPrice + suggestedPrice − comboPrice
 *
 * Shown only when savings ≥ R$ 1,00 and the combo is orderable right now;
 * among every candidate combo the anchor/suggested pair fits, the biggest
 * savings wins.
 */
export function findBestComboSavings(
  catalog: ComboCatalog,
  anchorItemId: string,
  anchorPriceReais: number,
  suggestedItemId: string,
  suggestedPriceReais: number,
): ComboSavings | null {
  let best: ComboSavings | null = null;
  const anchorPriceCents = toCents(anchorPriceReais);
  const suggestedPriceCents = toCents(suggestedPriceReais);

  for (const offer of catalog.itemOffers) {
    if (!offer.optionGroupsId || offer.optionGroupsId.length === 0) continue;

    const anchorMatch = findRequiredOption(offer, catalog.optionGroups, anchorItemId, null);
    if (!anchorMatch) continue;

    const suggestedMatch = findRequiredOption(offer, catalog.optionGroups, suggestedItemId, anchorMatch.group.id);
    if (!suggestedMatch) continue;

    const category = catalog.categoryByOfferId?.get(offer.id) ?? null;
    if (!isOfferAvailableNow(offer, catalog.availabilities, category)) continue;

    const skip = new Set([anchorMatch.group.id, suggestedMatch.group.id]);
    const comboPriceCents =
      toCents(offer.price.value) +
      toCents(anchorMatch.option.price.value) +
      toCents(suggestedMatch.option.price.value) +
      restOfRequiredGroupsMinPriceCents(offer, catalog.optionGroups, skip);

    const savingsCents = anchorPriceCents + suggestedPriceCents - comboPriceCents;
    if (savingsCents < COMBO_MIN_SAVINGS_CENTS) continue;
    if (best && savingsCents <= best.savingsCents) continue;

    best = {
      comboItemOfferId: offer.id,
      comboItemId: offer.itemId,
      anchorItemId,
      suggestedItemId,
      savingsCents,
    };
  }

  return best;
}
